<template>
  <div>
    <b-card>
      <b-row>
        <b-col md="6">
          <h1>Gestión de Docentes</h1>
        </b-col>
        <b-col md="6" class="text-right">
          <b-button variant="success" class="mr-1" @click="openPersonModal(null)">
            <feather-icon icon="UserPlusIcon" class="mr-50" />
            Nueva Persona
          </b-button>
          <b-button variant="primary" @click="openModal(null)">
            <feather-icon icon="PlusIcon" class="mr-50" />
            Nuevo Docente
          </b-button>
        </b-col>
      </b-row>

      <b-table
        striped
        hover
        responsive
        class="mt-2"
        :items="teachers"
        :fields="fields"
      >
        <template #cell(nombreCompleto)="data">
          {{ data.value || 'Sin asignar' }}
        </template>
        <template #cell(estado)="data">
          <b-badge :variant="data.value ? 'light-success' : 'light-danger'">
            {{ data.value ? 'Activo' : 'Inactivo' }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button variant="gradient-primary" class="btn-icon" @click="openModal(data.item)">
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button variant="gradient-danger" class="btn-icon ml-1" @click="deleteTeacher(data.item)">
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <teacher-modal
      :teacher-data="selectedTeacher"
      :personas="personas"
      @submit="handleModalSubmit"
    />
    
    <person-modal
      :person-data="selectedPerson"
      @submit="handlePersonModalSubmit"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, BBadge,
} from 'bootstrap-vue'
import TeacherModal from '@/components/teachers/TeacherModal.vue'
import PersonModal from '@/components/persons/PersonModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import catedraticosService from '@/services/catedraticosService'
import personasService from '@/services/personasService'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    TeacherModal,
    PersonModal,
  },
  data() {
    return {
      fields: [
        { key: 'especialidad', label: 'Especialidad', sortable: true },
        { key: 'nombreCompleto', label: 'Nombre', sortable: true },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'actions', label: 'Acciones' },
      ],
      teachers: [],
      personas: [],
      selectedTeacher: null,
      selectedPerson: null,
      loading: false,
    }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadTeachers(),
          this.loadPersonas()
        ])
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al cargar los datos',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async loadTeachers() {
      try {
        const response = await catedraticosService.getAll()
        this.teachers = response
      } catch (error) {
        console.error('Error al cargar catedráticos:', error)
        throw error
      }
    },
    async loadPersonas() {
      try {
        const response = await personasService.getAll()
        this.personas = response
      } catch (error) {
        console.error('Error al cargar personas:', error)
        throw error
      }
    },
    openModal(item) {
      this.selectedTeacher = item ? { ...item } : null
      this.$bvModal.show('teacher-modal')
    },
    openPersonModal(item) {
      this.selectedPerson = item ? { ...item } : null
      this.$bvModal.show('person-modal')
    },
    async handleModalSubmit(data) {
      try {
        if (data.catedraticoId) {
          // Actualizar catedrático existente
          await catedraticosService.update(data.catedraticoId, data)
        } else {
          // Crear nuevo catedrático
          await catedraticosService.create(data)
        }
        
        await this.loadTeachers()
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Operación Exitosa',
            icon: 'CheckIcon',
            variant: 'success',
            text: `El docente se ha ${data.catedraticoId ? 'actualizado' : 'creado'} correctamente.`,
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al procesar la operación',
          },
        })
      }
    },
    async deleteTeacher(item) {
      this.$swal({
        title: '¿Estás seguro?',
        text: '¡No podrás revertir esto!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, ¡elimínalo!',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-danger ml-1' },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          try {
            await catedraticosService.delete(item.catedraticoId)
            await this.loadTeachers()
            
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Eliminado',
                icon: 'TrashIcon',
                variant: 'success',
                text: 'El docente ha sido eliminado.',
              },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: 'Error al eliminar el docente',
              },
            })
          }
        }
      })
    },
    async handlePersonModalSubmit(data) {
      try {
        if (data.personaId) {
          // Actualizar persona existente
          await personasService.update(data.personaId, data)
        } else {
          // Crear nueva persona
          await personasService.create(data)
        }
        
        await this.loadPersonas()
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Operación Exitosa',
            icon: 'CheckIcon',
            variant: 'success',
            text: `La persona se ha ${data.personaId ? 'actualizado' : 'creado'} correctamente.`,
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al procesar la operación',
          },
        })
      }
    },
  },
}
</script>
